import { Breadcrumbs, Layout } from '@loadsmart/loadsmart-ui'
import { Bolt, Widgets } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Drawer,
  Grid,
  IconButton,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { IntegrationModuleList } from 'common/types/kraken-core/IntegrationModule'
import { PaginatedResponse } from 'common/types/kraken-core/Pagination'
import useDebouncedValue from 'hooks/useDebouncedValue'
import useTopNavigationContext from 'hooks/useTopNavigationContext/useTopNavigationContext'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getIntegrationModule,
  searchIntegrationModule,
} from 'services/kraken-core/integration_module/integration.service'
import IntegrationModuleDetail from './IntegrationModuleDetail'
import { useTabTitle } from 'hooks/useTabTitle/useTabTitle'
import mapping from 'atoms/Icon/mapping'
import Loading from 'atoms/Loading'
import IntegrationModuleInstallationWizard from './IntegrationModuleInstallationWizard'
import { useParams, useHistory } from 'react-router-dom'
import { ROUTES } from 'routing/routes'

export interface IntegrationModulesListProps {}

export default function IntegrationModulesList(props: IntegrationModulesListProps) {
  useTabTitle('Integration Modules')
  const { id } = useParams<{ id?: string }>()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [installationModalOpen, setInstallationModalOpen] = useState<boolean>(false)
  const [integrationModules, setIntegrationModules] = useState<
    PaginatedResponse<IntegrationModuleList>
  >()
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState<boolean>(false)
  const [selectedIntegrationModule, setSelectedIntegrationModule] = useState<
    IntegrationModuleList
  >()

  async function search() {
    setIsSearching(true)
    searchIntegrationModule({
      name: debouncedSearchTerm,
      page_size: 12,
    })
      .then(response => {
        response.response && setIntegrationModules(response.response.data)
      })
      .catch(error => {
        toast.error('Error listing integration modules')
      })
      .finally(() => {
        setIsSearching(false)
      })
  }

  const history = useHistory()

  function handleSelectIntegrationModule(integrationModule: IntegrationModuleList) {
    setSelectedIntegrationModule(integrationModule)
    setIsDetailDrawerOpen(true)

    // Set the URL to the detail view if an ID is present
    if (integrationModule.id) {
      history.push(`${ROUTES.INTEGRATION_MODULES}/${integrationModule.id}`)
    }
  }

  function handleCloseDetailDrawer() {
    setIsDetailDrawerOpen(false)
    setSelectedIntegrationModule(undefined)
    // Navigate back to the list view
    history.push(ROUTES.INTEGRATION_MODULES)
  }

  function installModule(integrationModule: IntegrationModuleList): void {
    if (!integrationModule || installationModalOpen) {
      return
    }

    setSelectedIntegrationModule(integrationModule)
    setInstallationModalOpen(true)
  }

  useEffect(() => {
    // Only handle the detail drawer logic here
    if (id && !selectedIntegrationModule) {
      getIntegrationModule(id).then(response => {
        const integrationModule = response.response?.data
        if (integrationModule) {
          setSelectedIntegrationModule(integrationModule)
          setIsDetailDrawerOpen(true)
        } else {
          toast.error('Integration Module not found')
        }
      })
    }
  }, [id])

  useEffect(() => {
    search()
  }, [debouncedSearchTerm])

  useTopNavigationContext({
    children: (
      <Layout.Group className="w-full" align="center">
        {mapping['IntegrationModule']}
        <Breadcrumbs
          entries={[
            {
              label: 'Integration Modules',
              active: true,
            },
          ]}
        />
      </Layout.Group>
    ),
  })

  return (
    <Stack
      spacing={2}
      margin={1}
      sx={{
        height: '90vh',
        overflow: 'hidden',
      }}
    >
      {/* Details */}
      <Drawer
        open={isDetailDrawerOpen}
        anchor="right"
        onClose={handleCloseDetailDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: '60%',
            padding: '20px',
          },
        }}
      >
        {selectedIntegrationModule ? (
          <IntegrationModuleDetail id={selectedIntegrationModule?.id} />
        ) : null}
      </Drawer>

      {/* Installation Wizard Modal */}
      <Modal
        open={installationModalOpen}
        onClose={() => {
          setInstallationModalOpen(false)
          setSelectedIntegrationModule(undefined)
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {selectedIntegrationModule ? (
          <IntegrationModuleInstallationWizard
            integrationModuleId={selectedIntegrationModule?.id}
            onClose={() => {
              setInstallationModalOpen(false)
              setSelectedIntegrationModule(undefined)
            }}
            onSuccess={() => {
              setInstallationModalOpen(false)
              setSelectedIntegrationModule(undefined)
            }}
            onError={error => {
              setInstallationModalOpen(false)
              setSelectedIntegrationModule(undefined)
              toast.error(error)
            }}
          />
        ) : (
          <></>
        )}
      </Modal>

      <TextField
        label="Search by keyword..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        sx={{
          mt: 1,
        }}
      />

      <Stack
        spacing={2}
        sx={{
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {/* Loading Spinner */}
        {isSearching ? <Loading /> : null}

        {/* Integration Modules */}
        {!isSearching && integrationModules && integrationModules?.results.length > 0 ? (
          <Grid container spacing={2}>
            {integrationModules?.results.map((integrationModule, index) => {
              return (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                  <CardActionArea
                    onClick={() => handleSelectIntegrationModule(integrationModule)}
                    sx={{
                      borderRadius: '20px',
                    }}
                  >
                    <Card
                      key={index}
                      sx={{
                        height: '200px', // Set a consistent height for all cards
                        borderRadius: '20px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          height: '100%', // Ensure the Box spans the full height of the Card
                          padding: 0,
                        }}
                      >
                        <CardHeader
                          title={
                            <Typography
                              variant="h3"
                              fontWeight={'bold'}
                              sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2, // Limit to 2 lines
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxHeight: '3em', // Ensure the title doesn't stretch
                              }}
                            >
                              {integrationModule.name}
                            </Typography>
                          }
                          avatar={
                            <Avatar
                              sizes="large"
                              sx={{
                                backgroundColor: 'secondary.main',
                              }}
                            >
                              <Widgets />
                            </Avatar>
                          }
                        />
                        <CardContent>
                          <Grid container spacing={1}>
                            <Grid item>
                              <Chip
                                label={integrationModule.capability?.name}
                                size="small"
                                variant="outlined"
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions sx={{ padding: 2 }}>
                          <Grid container justifyContent={'end'} alignItems={'end'} spacing={1}>
                            <Grid item>
                              <Tooltip title={'Install'} placement="top">
                                <IconButton
                                  onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    installModule(integrationModule)
                                  }}
                                  sx={{
                                    backgroundColor: theme => theme.palette.primary.main,
                                    color: theme => theme.palette.primary.contrastText,
                                    '&:hover': {
                                      backgroundColor: theme => theme.palette.primary.dark,
                                    },
                                  }}
                                >
                                  <Bolt />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </CardActions>
                      </Box>
                    </Card>
                  </CardActionArea>
                </Grid>
              )
            })}
          </Grid>
        ) : null}

        {/* No Integration Modules */}
        {!isSearching && (!integrationModules || integrationModules?.results.length === 0) ? (
          <Stack spacing={2} justifyContent={'start'} alignItems={'start'}>
            <Typography variant="body2">No Integration Modules found...</Typography>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  )
}
